<template>
	<form class="wide" @submit.prevent="submit">
		<loader v-if="!formReady"/>
		<template v-else>
			<fieldset>
				<div>
					<div>
						<legend><span>Details</span></legend>
					</div>
					<div>
						<label><span>First name </span>
							<div v-if="form.errors.get('profile.name_first')" class="error-block">
								<p v-html="form.errors.get('profile.name_first')"></p>
							</div>
							<div>
								<input v-model="form.data.profile.name_first" type="text">
							</div>
						</label>
						<label><span>Last name </span>
							<div v-if="form.errors.get('profile.name_last')" class="error-block">
								<p v-html="form.errors.get('profile.name_last')"></p>
							</div>
							<div>
								<input v-model="form.data.profile.name_last" type="text">
							</div>
						</label>
						<label><span>Email </span>
							<div v-if="form.errors.get('email')" class="error-block">
								<p v-html="form.errors.get('email')"></p>
							</div>
							<div>
								<input v-model="form.data.email" type="text">
							</div>
						</label>
						<label><span>Telephone </span>
							<div v-if="form.errors.get('profile.phone')" class="error-block">
								<p v-html="form.errors.get('profile.phone')"></p>
							</div>
							<div>
								<input v-model="form.data.profile.phone" type="text">
							</div>
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Roles</span></legend>
					</div>
					<div>
						<label><span>Roles </span>
							<div v-if="form.errors.get('roles')" class="error-block">
								<p v-html="form.errors.get('roles')"></p>
							</div>
							<div>
								<checkbox-manager
									:all-items="form.options.roles"
									:checked="form.optionsByFieldArray('roles', 'roles')"
									v-on:valuesChanged="(items) => form.set('roles', items)"/>
							</div>
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Settings</span></legend>
					</div>
					<div>
						<label><span>Notifications </span>
							<div>
								<checkbox-manager
									:all-items="form.options.notifications"
									:checked="form.optionsByFieldArray('notifications', 'settings.notifications')"
									v-on:valuesChanged="(items) => form.set('settings.notifications', items)"/>
							</div>
						</label>
						<label><span>Maximum Assigned Lead Quota </span>
							<div v-if="form.errors.get('settings.maxAssignedLeads')" class="error-block">
								<p v-html="form.errors.get('settings.maxAssignedLeads')"></p>
							</div>
							<div>
								<input v-model="form.data.settings.maximum_assigned_lead_quota" type="text">
							</div>
							<div class="help-block">
								<p>This determines the maximum number of leads that can be assigned to the user at any given time.</p>
								<p>If this <em>is not set</em> the
									<em>'default maximum assigned lead quota'</em> setting will be used.</p>
								<p>If the
									<em>'default maximum assigned lead quota' is not set</em> it will be assumed that there is
									<em>no maximum.</em></p>
							</div>
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Instruction Commissions</span></legend>
					</div>
					<div>
						<label><span>Services </span>
							<div>
								<checkbox-manager
									:all-items="form.options.providedServices"
									:checked="form.optionsByFieldKeys('providedServices', 'settings.instruction_commissions')"
									itemLabel="names.plural"
									v-on:valuesChanged="(items) => form.setKeys('settings.instruction_commissions', items)"/>
							</div>
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset
				v-for="(service, serviceIndex) in form.optionsByFieldKeys('providedServices', 'settings.instruction_commissions')"
				:key="serviceIndex">
				<div v-if="form.data.settings.instruction_commissions[service.id]">
					<div>
						<legend><span>{{ service.names.plural }} Instruction Commission Configuration</span>
						</legend>
					</div>
					<div class="input-group">
						<label><span>Commission Type </span>
							<select
								v-model="form.data.settings.instruction_commissions[service.id].commission_type">
								<option disabled selected value="">—</option>
								<option value="fixed">Fixed</option>
								<option value="percent">Percent</option>
							</select>
						</label>
						<label
							v-if="'fixed' === form.data.settings.instruction_commissions[service.id].commission_type"><span>Commission Amount (Fixed £) </span>
							<input
								v-model="form.data.settings.instruction_commissions[service.id].commission_price"
								type="number">
						</label>
						<label
							v-if="'percent' === form.data.settings.instruction_commissions[service.id].commission_type"><span>Commission Percent (Percentage %) </span>
							<input
								v-model="form.data.settings.instruction_commissions[service.id].commission_percent"
								type="number">
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Introduction Commissions</span></legend>
					</div>
					<div>
						<label><span>Services </span>
							<div>
								<checkbox-manager
									:all-items="form.options.introducedServices"
									:checked="form.optionsByFieldKeys('introducedServices', 'settings.introduction_commissions')"
									itemLabel="names.plural"
									v-on:valuesChanged="(items) => form.setKeys('settings.introduction_commissions', items)"/>
							</div>
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset
				v-for="(service, serviceIndex) in form.optionsByFieldKeys('introducedServices', 'settings.introduction_commissions')"
				:key="serviceIndex">
				<div v-if="form.data.settings.introduction_commissions[service.id]">
					<div>
						<legend>
							<span>{{ service.names.plural }} Introduction Commission Configuration</span>
						</legend>
					</div>
					<div class="input-group">
						<label><span>Commission Type </span>
							<select
								v-model="form.data.settings.introduction_commissions[service.id].commission_type">
								<option disabled selected value="">—</option>
								<option value="fixed">Fixed</option>
								<option value="percent">Percent</option>
							</select>
						</label>
						<label
							v-if="'fixed' === form.data.settings.introduction_commissions[service.id].commission_type"><span>Commission Amount (Fixed £) </span>
							<input
								v-model="form.data.settings.introduction_commissions[service.id].commission_price"
								type="number">
						</label>
						<label
							v-if="'percent' === form.data.settings.introduction_commissions[service.id].commission_type"><span>Commission Percent (Percentage %) </span>
							<input
								v-model="form.data.settings.introduction_commissions[service.id].commission_percent"
								type="number">
						</label>
					</div>
				</div>
			</fieldset>
			<fieldset>
				<div>
					<div>
						<legend><span>Availability</span></legend>
					</div>
					<div>
						<table>
							<thead>
								<tr>
									<th align="left" class="discreet"><span>Available</span></th>
									<th align="left">Day</th>
									<th align="center">From</th>
									<th align="center">To</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(day, dayIndex) in ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']"
									:key="dayIndex">
									<td align="left">
										<input
											v-model="form.data.settings.availability[dayIndex].available"
											type="checkbox">
									</td>
									<th align="left" scope="row" v-html="day"></th>
									<td align="center">
										<select v-model="form.data.settings.availability[dayIndex].from">
											<template v-for="n in 48">
												<option
													:selected="form.data.settings.availability[dayIndex].from"
													:value="hourFromInt(n)"
													v-html="hourFromInt(n)"/>
											</template>
										</select>
									</td>
									<td align="center">
										<select v-model="form.data.settings.availability[dayIndex].to">
											<template v-for="n in 48">
												<option
													:selected="form.data.settings.availability[dayIndex].to"
													:value="hourFromInt(n)"
													v-html="hourFromInt(n)"/>
											</template>
										</select>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</fieldset>
			<div class="actions">
				<ul>
					<li>
						<process-button
							:processing="form.processing" :text="$attrs.cta" class="button button--primary"/>
					</li>
				</ul>
			</div>
		</template>
	</form>
</template>
<script>
import {createNamespacedHelpers} from "vuex";

const {mapActions} = createNamespacedHelpers("users");

export default {

	data() {
		return {
			formReady: false,
			form: this.newForm({
				email: null,
				profile: {
					name_first: null,
					name_last: null,
					phone: null,
				},
				roles: [],
				settings: {
					notifications: [],
					maximum_assigned_lead_quota: null,
					instruction_commissions: [{
						commission_type: "",
						commission_price: null,
						commission_percent: null,
					}],
					introduction_commissions: [{
						commission_type: "",
						commission_price: null,
						commission_percent: null,
					}],
					availability: {
						0: {
							available: true,
							from: "09:00",
							to: "17:00",
						},
						1: {
							available: true,
							from: "09:00",
							to: "17:00",
						},
						2: {
							available: true,
							from: "09:00",
							to: "17:00",
						},
						3: {
							available: true,
							from: "09:00",
							to: "17:00",
						},
						4: {
							available: true,
							from: "09:00",
							to: "17:00",
						},
						5: {
							available: true,
							from: "09:00",
							to: "17:00",
						},
						6: {
							available: false,
							from: "09:00",
							to: "17:00",
						},
					},
				},
			}),
		};
	},

	props: {
		user: null,
		method: {
			type: String,
			default() {
				return "post";
			},
		},
	},

	methods: {
		...mapActions([
			"addModel",
			"updateModel",
		]),
		hourFromInt(n) {
			let hour = n % 2 == 0 ? n - ((n / 2) + 1) : n - (Math.ceil(n / 2));
			hour = this.pad(hour, 2);
			let min = n % 2 == 0 ? "30" : "00";
			return [hour, min].join(":");
		},
		pad(n, width, z) {
			z = z || "0";
			n = n + "";
			return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
		},
		async submit() {
			if ("post" === this.method) {
				this.form.post("users").then(response => {
					this.$router.replace({
						name: "users.edit", params: {
							"user": response.data.id,
						},
					});
				});
			}

			if ("patch" === this.method) {
				this.form.patch("users/" + this.user.id).then(data => {
					this.updateModel(data.data);
					this.$store.dispatch("alertsStore/success", "User Updated");
				});
			}
		},
	},

	created() {
		http().get(this.route("form-data/user/:user", this.user)).then(response => {
			this.form.setOptions(response.data);

			if (this.user) {
				this.form.prefill(this.user);

				this.form.set("roles", _.map(this.user.roles, "name"));

				this.form.set("settings.notifications", this.user.settings.notifications);
			}

			this.formReady = true;
		});
	},
};
</script>
